import { useState } from 'react'
import { IPaginationProps } from './types'
import styles from './styles.module.scss'
import clsx from 'clsx'

const Pagination = ({
  currentPage = 1,
  itemsPerPage = 10,
  maxPagesShown = 10,
  onPageChange,
  style,
  totalItems,
  ...props
}: IPaginationProps) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const [selectedPage, setSelectedPage] = useState(currentPage)

  const handlePageChange = (page: number) => {
    setSelectedPage(page)
    onPageChange(page)
  }

  // Calculate the starting and ending points for the displayed page numbers.
  let startPage = 1
  let endPage = totalPages

  if (maxPagesShown && maxPagesShown < totalPages) {
    const maxPagesOnEachSide = Math.floor((maxPagesShown - 1) / 2)
    startPage = Math.max(
      1,
      Math.min(selectedPage - maxPagesOnEachSide, totalPages - maxPagesShown + 1),
    )
    endPage = Math.min(totalPages, startPage + maxPagesShown - 1)
  }
  return (
    <div
      className={clsx('mui-pagination', styles.pagination)}
      data-testid="mui-pagination"
      style={style}
      {...props}
    >
      {/* Back to start */}
      <div className={styles.item}>
        <button
          className={styles.button}
          disabled={selectedPage <= 1 ? true : false}
          type="button"
          onClick={() => handlePageChange(1)}
          aria-label="First Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
            <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 278.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192zm384-192l-192 192c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L301.3 256 470.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0z" />
          </svg>
        </button>
      </div>
      {/* Back to one page */}
      <div className={styles.item}>
        <button
          className={styles.button}
          disabled={selectedPage <= 1 ? true : false}
          type="button"
          onClick={() => handlePageChange(selectedPage - 1)}
          aria-label="Previous Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 320 512">
            <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
          </svg>
        </button>
      </div>

      <div className={styles.smallPagesIndicator} data-testid="small-pages-indicator">
        {selectedPage} of {totalPages}
      </div>

      <div className={styles.pageNumbers} data-testid="page-numbers">
        {startPage > 2 && <div className={styles.item}>...</div>}

        {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
          const page = startPage + index
          return (
            <div className={styles.item} key={page}>
              <button
                className={clsx(styles.button, {
                  [styles.active]: Boolean(selectedPage === page),
                })}
                onClick={() => handlePageChange(page)}
                type="button"
              >
                <span>{page}</span>
              </button>
            </div>
          )
        })}

        {endPage < totalPages - 1 && <div className={styles.item}>...</div>}
      </div>

      {/* Forward One Page */}
      <div className={styles.item}>
        <button
          className={styles.button}
          disabled={selectedPage === totalPages ? true : false}
          type="button"
          onClick={() => handlePageChange(selectedPage + 1)}
          aria-label="Next Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 320 512">
            <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
          </svg>
        </button>
      </div>
      {/* Forward to end  */}
      <div className={styles.item}>
        <button
          className={styles.button}
          disabled={selectedPage === totalPages ? true : false}
          type="button"
          onClick={() => handlePageChange(totalPages)}
          aria-label="Last Page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
            <path d="M470.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-192-192c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 256 233.4 425.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l192-192zm-384 192l192-192c12.5-12.5 12.5-32.8 0-45.3l-192-192c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L210.7 256 41.4 425.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0z" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default Pagination
